
/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { concatMap, map, mergeMap } from 'rxjs/operators';
import { AbstractObject } from '@src/app/model/abstract-object';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { ActionColor, IFormAction } from '@src/app/shared/form/form.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { MemoField } from "@src/app/shared/memos/MemoField";
import { RequestService } from '@src/app/modules/crm/request.service';
import { ResolveRequestComponent } from '@src/app/modules/crm/service-request-page/resolve-request/resolve-request.component';
import { MyInjector } from '@src/app/app.module';
import { ReopenRequestComponent } from '@src/app/modules/crm/service-request-page/reopen-request/reopen-request.component';
import { ResolveWorkOrderComponent } from '@src/app/modules/crm/service-request-page/resolve-work-order/resolve-work-order.component';
import { RequestContextService } from '@src/app/modules/crm/request-context.service';
import { RequestTemplatesService } from '@src/app/modules/crm/request-templates.service';
import { RequestTemplate } from '@src/app/model/RequestTemplate';
import { DateHelper } from '@src/app/shared/dateHelper';

export class ResolveRequestAction implements IFormAction {
    name = $localize`Close`;
    color: ActionColor = 'primary';
    show = false;
    icon = 'done_all';
    approvalNeeded = false;
    disabled = false;
    approvalText: string;
    tipText: string;

    sr: ServiceRequest;
    requestTemplateSvc = MyInjector.instance.get(RequestTemplatesService);
    reqContextSvc = MyInjector.instance.get(RequestContextService);

    constructor(private svc: RequestService, private dialog: MatDialog) { }

    /**
     * A template can name the standard request that follows it, so closing this one raises that one -
     * locked to the follow on template, and planned the number of days out the template asks for.
     * The request's teamId is the OMC's own team, which is all the stepper needs to place it.
     */
    createNext(sr: ServiceRequest) {
        return this.requestTemplateSvc.getOne<RequestTemplate>(sr.templateId, null).pipe(concatMap(rt => {
            if (!rt?.followOnId) {
                return of(sr);
            }
            return this.reqContextSvc.newRequest({
                requestType: 'case',
                teamId: sr.teamId,
                templateId: rt.followOnId,
                unit: sr.unit,
                person: sr.person,
                plannedDate: DateHelper.today(rt.followOnAfter),
            }).pipe(map(() => sr));
        }));
    }

    action() {
        const dialogRef = this.sr.requestType === ServiceRequest.typeWorkOrder.id
            ? this.dialog.open(ResolveWorkOrderComponent, { data: { sr: this.sr } })
            : this.dialog.open(ResolveRequestComponent, { data: this.sr });

        return dialogRef.afterClosed().pipe(concatMap(r => {
            if (r) {
                if(r.templateId) {
                    return this.createNext(r);
                }
            }
            return of (r);
        }));
    }

    setup(sr: ServiceRequest) {
        this.sr = sr;
        if (sr.srStatus === ServiceRequest.srOpen.id) {
            this.show = true;
            if (sr.subStatus === ServiceRequest.srUnplanned.id) {
                this.disabled = false;
                this.tipText = $localize`Click to mark request as completed`;
            } else {
                this.disabled = true;
                this.tipText = $localize`Cannot mark request complete until actions are completed`;
            }
        } else {
            this.show = false;
        }
    }
}

export class ReOpenRequestAction implements IFormAction {
    name = $localize`Reopen`;
    color: ActionColor = 'primary';
    show = false;
    icon = 'lock_open_right';
    approvalNeeded = false;
    disabled = false;
    approvalText = $localize`Reopen this request for further actions`;
    tipText: string;
    sr: ServiceRequest;

    action() {
        const dialog = MyInjector.instance.get(MatDialog);
        const dialogRef = dialog.open(ReopenRequestComponent, { data: this.sr });
        return dialogRef.afterClosed();
    }
    //  setup(serviceRequest: ServiceRequest) {
    setup(sr: ServiceRequest) {
        this.sr = sr;
        if (sr.srStatus === ServiceRequest.srResolved.id) {
            this.show = true;
        } else {
            this.show = false;
        }
    }
}

export class AcceptRequestAction implements IFormAction {
    name = $localize`Accept`;
    color: ActionColor = 'primary';
    show = false;
    icon = 'thumb_up';
    approvalNeeded = true;
    disabled = false;
    approvalText: string;
    tipText: string;
    sr: ServiceRequest;

    /**
     * Works from the record setup() was given, not from the argument: form-top-actions hands an
     * action the form value, which drops every field with sendServer false - requestType among
     * them, because it is a pill.
     */
    action() {
        const rs = MyInjector.instance.get(RequestService);
        return rs.acceptRequest(this.sr)
    }
    //  setup(serviceRequest: ServiceRequest) {
    setup(sr: ServiceRequest) {
        this.sr = sr;
        this.approvalText = this.confirmText(sr);
        if (sr.srStatus === ServiceRequest.srResolved.id) {
            this.show = true;
        } else {
            this.show = false;
        }
    }

    /**
     * A work order is accepted by the charge for it - see PurchaseController::linkRequest - so
     * accepting one by hand says the opposite: nothing is coming.
     */
    private confirmText(sr: ServiceRequest) {
        if (sr.requestType === ServiceRequest.typeWorkOrder.id) {
            return $localize`Mark this work order accepted? There will be no charge for the work done
                under it. If an invoice or accrual is expected, enter that against this work order
                instead - it accepts the work order as it is saved.`;
        }
        return $localize`Are you sure you wish to accept this request? Once accepted it can not be reopened or modified. The original requestor should accept the request, or at least have been notified and given time to object before accepting`;
    }
}
export class AddActionFormAction implements IFormAction {
    name = $localize`Add Action`;
    color: ActionColor = 'primary';
    show = false;
    icon = 'format_list_bulleted_add';
    approvalNeeded = false;
    disabled = false;
    approvalText = '';
    tipText: string;

    constructor(private addFn: () => Observable<AbstractObject>) { }

    action(sr: ServiceRequest, config: FormConfig) {
        //const update = { id: sr.id, revision: sr.revision, srStatus: ServiceRequest.srOpen.id } as ServiceRequest;
        return this.addFn().pipe(
            mergeMap((action) => {
                return of(action).pipe(() => config.service.getOne(sr.id, null));
            }));
    }
    //  setup(serviceRequest: ServiceRequest) {
    setup(sr: ServiceRequest) {
        if (sr.srStatus === ServiceRequest.srOpen.id) {
            this.show = true;
        } else {
            this.show = false;
        }
    }
}
export class AddMemoAction implements IFormAction {
    name = $localize`Add Note`;
    color: ActionColor = 'primary';
    show = true;
    icon = 'add_notes';
    approvalNeeded = false;
    disabled = false;
    approvalText = '';
    tipText = 'Click to add note visible to agency staff';

    sr: ServiceRequest;

    constructor(private memoField: MemoField) { }

    action() {
        return this.memoField.createNewMemo().pipe(map(newNote => {
            if (newNote) {
                this.sr.memos.push(newNote);
                return this.sr;
            }
        }));
    }

    setup(sr: ServiceRequest) {
        this.sr = sr;
    }
}
